import { useState, useRef, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  AvatarGroup,
  useTheme,
  IconButton,
  Fade,
  TextField,
  Menu,
  MenuItem,
  Popover,
  Checkbox,
  InputBase,
  ClickAwayListener,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Draggable } from "react-beautiful-dnd";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
// import { useGetCompanyMembersQuery } from "@/features/Members/api";
import {
  severityColorMap,
  severityMap,
  typeToIconMapping,
  getSeverityIcon,
  getBackgroundColor,
} from "../utils";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetCommentsByBugIdQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useDeleteBugMutation,
  useGetListOfMembersQuery,
} from "@/features/bughurd/api";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Search, Clear, ChatBubbleOutline } from "@mui/icons-material";
import { useUpdateBugDetailMutation } from "@/features/bughurd/api";
import { toast } from "react-toastify";
import { SubtaskCreationCard } from "./SubTaskCard";
import { DatePickerPopover } from "./DatePickerPopover";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import { Subtask } from "./SubTaskDetail";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";
import { useAppSelector } from "@/app/hooks";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import {
  selectApiData,
  selectGroupingConfig,
} from "@/features/bughurd/boardSelector";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";
import LinkIcon from "@mui/icons-material/Link";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useLazyGetBugDetailByBugIdQuery } from "@/features/bughurd/api";

interface TaskProps {
  task: any;
  index: number;
  refreshColumn?: () => void;
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    newColumnId?: string,
  ) => void;
  availableStatus?: any;
  reactFlow?: any;
}

interface CommentsPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  comments: any[];
  isLoading: boolean;
  taskName: string;
  taskId: string;
  refreshColumn?: () => void;
  onTaskUpdate?: (taskId: string, updatedTask: any) => void;
  task?: any;
}

// Optimistic update hook
const useOptimisticUpdate = (initialValue: any) => {
  const [optimisticValue, setOptimisticValue] = useState(initialValue);
  const [isUpdating, setIsUpdating] = useState(false);

  const updateOptimistically = useCallback(
    (newValue: any, updateFn: () => Promise<any>) => {
      const previousValue = optimisticValue;

      // Apply optimistic update immediately
      setOptimisticValue(newValue);
      setIsUpdating(true);

      // Queue the actual API call
      const wrappedUpdateFn = async () => {
        try {
          const result = await updateFn();
          setIsUpdating(false);
          return result;
        } catch (error) {
          // Revert optimistic update on error
          setOptimisticValue(previousValue);
          setIsUpdating(false);
          throw error;
        }
      };

      // Execute the update without blocking UI
      wrappedUpdateFn().catch((error) => {
        console.error("Optimistic update failed:", error);
      });
    },
    [optimisticValue],
  );

  const setOptimisticValueDirect = useCallback((newValue: any) => {
    setOptimisticValue(newValue);
  }, []);

  return [
    optimisticValue,
    updateOptimistically,
    isUpdating,
    setOptimisticValueDirect,
  ];
};

export const formatDate = (dateString: string, format = "short"): string => {
  if (!dateString) return "";

  if (format === "relative") {
    const date: any = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now: any = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  } else {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
};

const getInitials = (name: string): string => {
  return name
    ?.split(" ")
    ?.map((part) => part[0])
    ?.join("")
    ?.toUpperCase()
    ?.slice(0, 2);
};

const stripHtmlTags = (html: string): string => {
  if (!html) return "";
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const CommentsPopover: React.FC<CommentsPopoverProps> = ({
  anchorEl,
  onClose,
  comments,
  isLoading,
  taskName,
  taskId,
  onTaskUpdate,
  task,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [commentInput, setCommentInput] = useState<string>("");
  const [createComment, { isLoading: isSubmitting }] =
    useCreateCommentMutation();
  const [updateComment, { isLoading: isUpdating }] = useUpdateCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommentInput(e.target.value);
  };

  const handleAddComment = async () => {
    if (!commentInput.trim() || !taskId) return;

    try {
      const payload = {
        itemId: taskId,
        comment: commentInput.trim(),
        module: "BUG_TRACKING",
        isComment: true,
        type: "PUBLIC",
        //    jiraObj: {
        //   itemkey: bugDetailData?.data?.decryptedData?.jiraConfig?.itemKey,
        // },
      };

      // Optimistic update - update comment count immediately
      if (onTaskUpdate && task) {
        onTaskUpdate(taskId, {
          ...task,
          commentCount: (task.commentCount || 0) + 1,
        });
      }

      await createComment(payload).unwrap();

      setCommentInput("");
      toast.success(t("Comment added successfully"));
    } catch (error: any) {
      console.error("Error adding comment:", error);

      // Revert optimistic update on error
      if (onTaskUpdate && task) {
        onTaskUpdate(taskId, {
          ...task,
          commentCount: Math.max((task.commentCount || 0) - 1, 0),
        });
      }

      toast.error(
        t("Failed to add comment") +
          ": " +
          (error.data?.message || t("Unknown error")),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleAddComment();
    }
  };

  const handleEditComment = (commentId: string, commentText: string) => {
    setEditingCommentId(commentId);
    setEditCommentText(commentText);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleSaveEdit = async () => {
    if (!editCommentText.trim() || !editingCommentId) return;

    try {
      await updateComment({
        commentId: editingCommentId,
        comment: editCommentText.trim(),
      }).unwrap();

      setEditingCommentId(null);
      setEditCommentText("");
      toast.success(t("Comment updated successfully"));
    } catch (error: any) {
      console.error("Error updating comment:", error);
      toast.error(
        t("Failed to update comment") +
          ": " +
          (error.data?.message || t("Unknown error")),
      );
    }
  };

  const openDeleteModal = (commentId: string) => {
    setCommentToDelete(commentId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      // Optimistic update - update comment count immediately
      if (onTaskUpdate && task) {
        onTaskUpdate(taskId, {
          ...task,
          commentCount: Math.max((task.commentCount || 0) - 1, 0),
        });
      }

      await deleteComment(commentToDelete).unwrap();

      toast.success(t("Comment deleted successfully"));
      closeDeleteModal();
    } catch (error: any) {
      console.error("Error deleting comment:", error);

      // Revert optimistic update on error
      if (onTaskUpdate && task) {
        onTaskUpdate(taskId, {
          ...task,
          commentCount: (task.commentCount || 0) + 1,
        });
      }

      toast.error(
        t("Failed to delete comment") +
          ": " +
          (error.data?.message || t("Unknown error")),
      );
      closeDeleteModal();
    }
  };

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 400,
            borderRadius: 2,
            maxHeight: "calc(100vh - 100px)",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {taskName
              ? t('Comments on "{{taskName}}"', { taskName })
              : t("Comments")}
          </Typography>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
                flexGrow: 1,
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "auto",
                  mt: 1,
                  mb: 2,
                  maxHeight: "400px",
                  minHeight: "100px",
                }}
              >
                {comments && comments.length > 0 ? (
                  comments.map((comment) => (
                    <Box
                      key={comment._id}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            background: "primary.main",
                          }}
                        >
                          {comment.createdBy?.fullName?.charAt(0) || "U"}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              sx={{ mr: 1 }}
                            >
                              {comment.createdBy?.fullName || t("Unknown User")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mr: 1 }}
                            >
                              {formatDate(comment.createdAt, "relative")}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row-reverse",
                                width: "50%",
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleEditComment(
                                    comment._id,
                                    comment.comment,
                                  )
                                }
                                sx={{
                                  padding: "2px",
                                  color: "primary.main",
                                  "&:hover": { color: "primary.dark" },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => openDeleteModal(comment._id)}
                                sx={{
                                  padding: "2px",
                                  color: "error.main",
                                  "&:hover": { color: "error.dark" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {editingCommentId === comment._id ? (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                fullWidth
                                multiline
                                size="small"
                                value={editCommentText}
                                onChange={(e) =>
                                  setEditCommentText(e.target.value)
                                }
                                sx={{ mb: 1 }}
                                autoFocus
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={isUpdating}
                                >
                                  {t("Cancel")}
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleSaveEdit}
                                  disabled={
                                    !editCommentText.trim() || isUpdating
                                  }
                                >
                                  {isUpdating ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    t("Save")
                                  )}
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                mt: 0.5,
                              }}
                            >
                              {comment.comment}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      color: "text.secondary",
                    }}
                  >
                    <ChatBubbleOutline
                      sx={{ fontSize: 40, mb: 1, opacity: 0.5 }}
                    />
                    <Typography>{t("No comments yet")}</Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: "auto",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  pt: 2,
                }}
              >
                <Box
                  component="form"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAddComment();
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder={t("Add a comment...")}
                    value={commentInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                    disabled={isSubmitting}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t("Press Ctrl+Enter to submit")}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!commentInput.trim() || isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        t("Comment")
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Popover>

      <DeleteConfirmModal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteComment}
        loading={isDeleting}
        title={t("Delete Comment")}
        confirmMessage={t("Are you sure you want to delete this comment?")}
        okText={t("Delete")}
      />
    </>
  );
};

const Task: React.FC<TaskProps> = ({
  task,
  index,
  refreshColumn,
  onTaskUpdate,
  availableStatus,
  reactFlow,
}) => {
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal,
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const pageUrl = window.location.pathname;
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const { subGroupBy } = groupingConfig;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const { data: companyMemberData } = useGetListOfMembersQuery({
    projectId: id,
    page: 1,
    size: 100,
    searchName: "",
  });
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleteLoading }] = useDeleteBugMutation();
  const [isDeleteReply, setIsDeleteReply] = useState<boolean>(false);
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const [editingDescription, setEditingDescription] = useState<boolean>(false);
  const [titleValue, setTitleValue] = useState<string>("");
  const [descriptionValue, setDescriptionValue] = useState<string>("");
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const [isLoadingSubtasks, setIsLoadingSubtasks] = useState<boolean>(false);

  const [priorityAnchorEl, setPriorityAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [commentsAnchorEl, setCommentsAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] =
    useState<HTMLElement | null>(null);
  const [showSubtaskCreation, setShowSubtaskCreation] =
    useState<boolean>(false);
  const [doneStatusAnchorEl, setDoneStatusAnchorEl] =
    useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showSubtasks, setShowSubtasks] = useState<boolean>(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const subtaskContainerRef = useRef<HTMLDivElement>(null);
  const apiData = useAppSelector(selectApiData);
  const { projectDetails } = apiData;
  const doneStatusId =
    projectDetails?.doneStatus?._id || projectDetails?.doneStatus || "";
  const projectId = projectDetails?._id || task?.project?._id || task?.project;
  // Use optimistic updates for task data
  const [
    optimisticTask,
    updateTaskOptimistically,
    isOptimisticUpdating,
    setOptimisticTask,
  ] = useOptimisticUpdate(task);
  // Import lazy hook
  const [fetchBugDetail] = useLazyGetBugDetailByBugIdQuery();
  const [subTaskData, setSubTaskData] = useState<any>(
    optimisticTask?.subTasks || [],
  );
  const subtasksCount = optimisticTask?.subTasksCount || 0;

  const { data: commentsData, isLoading: isLoadingComments } =
    useGetCommentsByBugIdQuery(
      { bugId: optimisticTask._id },
      {
        skip: !optimisticTask._id || !commentsAnchorEl,
        refetchOnMountOrArgChange: true,
      },
    );

  const comments = commentsData?.data?.decryptedData?.items || [];
  useEffect(() => {
    if (optimisticTask?.subTasks && optimisticTask.subTasks.length > 0) {
      setSubTaskData(optimisticTask.subTasks);
    }
  }, [optimisticTask?.subTasks]);
  // Update optimistic task when prop changes
  useEffect(() => {
    if (task && JSON.stringify(task) !== JSON.stringify(optimisticTask)) {
      setOptimisticTask(task);
    }
  }, [task, optimisticTask, setOptimisticTask]);

  const getAssigneeData = (assignee: any) => {
    // If assignee is already an object with _id and fullName, return it
    if (typeof assignee === "object" && assignee._id && assignee.fullName) {
      return assignee;
    }

    // If assignee is just an ID string, look it up in companyMemberData
    const userId = typeof assignee === "string" ? assignee : assignee?._id;

    const memberItem = companyMemberData?.data?.decryptedData?.items?.find(
      (member: any) => member?.user?._id === userId || member?._id === userId,
    );

    if (!memberItem) {
      return null;
    }

    // Handle nested user structure
    const user = memberItem.user || memberItem;
    return {
      _id: user._id,
      fullName:
        user.fullName ||
        `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      email: user.email,
    };
  };

  const getProjectFilteredUsers = () => {
    return (
      companyMemberData?.data?.decryptedData?.items
        ?.filter(
          (item) =>
            item?.userRole === "TEAM_MEMBER" || item?.userRole === "TEAM_LEAD",
        )
        ?.map((item) => ({
          _id: item?.user?._id || item?._id,
          fullName:
            `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
          email: item?.user?.email,
          userRole: item?.userRole,
        })) || []
    );
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const convertMinutesToTimeString = (totalMinutes) => {
    if (!totalMinutes || totalMinutes === 0) return "";

    const days = Math.floor(totalMinutes / (12 * 60)); // Assuming 8-hour workday
    const hours = Math.floor((totalMinutes % (12 * 60)) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(" ");
  };
  const isDueDateApproaching = (): boolean => {
    if (!optimisticTask?.dueDate) return false;
    const dueDate = new Date(optimisticTask.dueDate);
    const today = new Date();
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 2 && diffDays >= 0;
  };

  const typeInfo = optimisticTask?.type
    ? typeToIconMapping[optimisticTask.type]
    : typeToIconMapping.TASK;
  const tags = Array.isArray(optimisticTask?.tags) ? optimisticTask.tags : [];
  const priorityLabel = t(severityMap[optimisticTask?.severity] || "None");
  const priorityColor =
    severityColorMap[optimisticTask?.severity] || theme.palette.grey[500];

  const toggleSubtasks = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const opening = !showSubtasks;
    setShowSubtasks(opening);

    if (
      opening &&
      (!optimisticTask.subTasks || optimisticTask.subTasks.length === 0)
    ) {
      try {
        setIsLoadingSubtasks(true); // Start loading
        const bugId = optimisticTask._id || optimisticTask.id;
        const result = await fetchBugDetail({
          bugId,
          withDetails: true,
        }).unwrap();

        const newSubtasks = result?.data?.decryptedData?.subTasks ?? [];
        const optimisticUpdate = {
          ...optimisticTask,
          subTasks: [...newSubtasks],
        };

        setSubTaskData(newSubtasks);
        setOptimisticTask(optimisticUpdate);
        setIsLoadingSubtasks(false); // End loading
      } catch (error) {
        console.error("Failed to fetch subtasks:", error);
        setIsLoadingSubtasks(false); // End loading on error
      }
    }
  };

  const handleMarkComplete = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const fallbackDoneStatusId = availableStatus?.find(
      (status) => status.status === "DONE",
    )?._id;
    const targetDoneStatusId = doneStatusId || fallbackDoneStatusId;

    if (!targetDoneStatusId) {
      toast.error(t("Done status is not configured for this project"));
      return;
    }

    const optimisticUpdate = {
      ...optimisticTask,
      bugStatus: targetDoneStatusId,
    };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        bugStatus: targetDoneStatusId,
      }).unwrap();

      toast.success(t("Task marked as complete"));

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate, targetDoneStatusId);
      } else if (refreshColumn) {
        refreshColumn();
      }

      return result;
    });
  };

  const handleGoToProjectSettings = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDoneStatusAnchorEl(null);
    if (projectId) {
      navigate(`/defect-tracker/projects/${projectId}/settings`);
      return;
    }
    navigate("/defect-tracker/settings");
  };

  const handleDoneStatusIconClick = (e: React.MouseEvent<HTMLElement>) => {
    if (doneStatusId) {
      handleMarkComplete(e as React.MouseEvent);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setDoneStatusAnchorEl(e.currentTarget);
  };

  const handleDoneStatusMenuClose = () => {
    setDoneStatusAnchorEl(null);
  };

  const handleAddSubtask = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSubtaskCreation(true);
  };

  const handleCloseSubtaskCreation = () => {
    setShowSubtaskCreation(false);
  };

  const handleSaveSubtask = async (subtaskData: any) => {
    try {
      setShowSubtaskCreation(false);
      const formattedSubtaskData = {
        ...subtaskData,
        estimate:
          typeof subtaskData.estimate === "number"
            ? convertMinutesToTimeString(subtaskData.estimate)
            : subtaskData.estimate,
      };

      const optimisticUpdate = {
        ...optimisticTask,
        subTasks: [...(optimisticTask.subTasks || []), formattedSubtaskData],
      };

      // Update local state immediately
      setOptimisticTask(optimisticUpdate);

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }
    } catch (error: any) {
      console.error("Error creating subtask:", error);
      toast.error(t("Failed to create subtask"));
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTitle(true);
    setTitleValue(optimisticTask?.summary || "");
  };

  const handleMoreOptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMoreOptionsAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleCloseMoreOptions = () => {
    setMoreOptionsAnchorEl(null);
  };

  const handleDeleteOption = () => {
    setMoreOptionsAnchorEl(null);
    setIsDeleteReply(true);
  };

  const handleEditOption = () => {
    setMoreOptionsAnchorEl(null);
    navigate(pageUrl + "?bugId=" + task?._id);
    // setIsModalOpen(true);
  };

  const handleDelete = () => {
    // Optimistic update - notify parent immediately
    if (onTaskUpdate) {
      onTaskUpdate(optimisticTask._id, null); // null indicates deletion
    }

    deleteBug(optimisticTask?._id || optimisticTask?.id)
      .unwrap()
      .then(() => {
        setIsDeleteReply(false);
        toast.success(t("Bug deleted successfully"));

        // No need to update parent again as it was done optimistically
        if (refreshColumn && !onTaskUpdate) {
          refreshColumn();
        }
      })
      .catch((error: any) => {
        console.error("Error deleting task:", error);
        toast.error(
          t("Failed to delete task") + ": " + (error.data?.message || t("Unknown error")),
        );
        setIsDeleteReply(false);

        // Revert optimistic update by refreshing
        if (refreshColumn) {
          refreshColumn();
        }
      });
  };

  const handleSaveTitleEdit = async () => {
    if (!titleValue.trim()) {
      setEditingTitle(false);
      return;
    }

    const optimisticUpdate = { ...optimisticTask, summary: titleValue };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        summary: titleValue,
      }).unwrap();

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Title updated successfully"));
      return result;
    });

    setEditingTitle(false);
  };

  const handleSaveDescriptionEdit = async () => {
    const optimisticUpdate = {
      ...optimisticTask,
      description: descriptionValue,
    };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        description: descriptionValue,
      }).unwrap();

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Description updated successfully"));
      return result;
    });

    setEditingDescription(false);
  };

  const startEditingDescription = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDescription(true);
    setDescriptionValue(stripHtmlTags(optimisticTask?.description) || "");
  };

  const handleDueDateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDateAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleSeverityClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPriorityAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleAssigneesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAssigneeAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleCommentsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentsAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleCloseComments = () => {
    setCommentsAnchorEl(null);
  };

  const handleDateSelect = async (date: Date | null) => {
    const optimisticUpdate = { ...optimisticTask, dueDate: date };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        dueDate: date,
      }).unwrap();

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Due date updated successfully"));
      return result;
    });

    setDateAnchorEl(null);
  };

  const handlePriorityChange = async (newPriority: string) => {
    const optimisticUpdate = { ...optimisticTask, severity: newPriority };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        severity: newPriority,
      }).unwrap();

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Priority updated successfully"));
      return result;
    });

    setPriorityAnchorEl(null);
  };

  const handleAssigneeChange = async (userId: string) => {
    const currentAssignees = optimisticTask.assignTo || [];

    // Normalize current assignees to get their IDs
    const currentAssigneeIds = currentAssignees
      .map((assignee: any) => {
        const assigneeData = getAssigneeData(assignee);
        return assigneeData?._id;
      })
      .filter(Boolean);

    const userIndex = currentAssigneeIds.indexOf(userId);
    let updatedAssignees;

    if (userIndex >= 0) {
      // Remove user - keep full objects for those remaining
      updatedAssignees = currentAssignees
        .map((assignee: any) => getAssigneeData(assignee))
        .filter((assignee: any) => assignee && assignee._id !== userId);
    } else {
      // Add user - get full object
      const userToAdd = getProjectFilteredUsers().find(
        (u: any) => u._id === userId,
      );
      if (userToAdd) {
        const newAssignee = {
          _id: userId,
          fullName: userToAdd.fullName,
          email: userToAdd.email,
        };
        // Normalize existing assignees and add new one
        const normalizedAssignees = currentAssignees
          .map((assignee: any) => getAssigneeData(assignee))
          .filter(Boolean);
        updatedAssignees = [...normalizedAssignees, newAssignee];
      } else {
        updatedAssignees = currentAssignees;
      }
    }

    const optimisticUpdate = { ...optimisticTask, assignTo: updatedAssignees };

    updateTaskOptimistically(optimisticUpdate, async () => {
      const result = await updateBug({
        bugId: optimisticTask._id,
        assignTo: updatedAssignees.map((u: any) => u._id),
      }).unwrap();

      // Update parent component state
      if (onTaskUpdate) {
        onTaskUpdate(optimisticTask._id, optimisticUpdate);
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Assignees updated successfully"));
      return result;
    });

    setAssigneeAnchorEl(null);
  };

  // Updated to use project filtered users - UPDATED
  const filteredUsers = getProjectFilteredUsers()?.filter((user: any) =>
    user.fullName?.toLowerCase().includes(searchQuery?.toLowerCase()),
  );
  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [editingTitle]);

  useEffect(() => {
    if (editingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [editingDescription]);

  const handleCardClick = () => {
    if (!editingTitle && !editingDescription) {
      navigate(pageUrl + "?bugId=" + task?._id);
      // setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (!showSubtaskCreation) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        showSubtaskCreation &&
        subtaskContainerRef.current &&
        !subtaskContainerRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(".MuiPopover-root") &&
        !(event.target as Element).closest(".MuiMenu-root")
      ) {
        setShowSubtaskCreation(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSubtaskCreation]);

  // Helper function to get dependency counts
  const getDependencyCounts = (linkBugs: any[], relatedBugsCount: number) => {
    const counts = {
      waiting: 0,
      blocked: 0,
      linked: 0,
      related: relatedBugsCount,
    };

    linkBugs?.forEach((linkBug) => {
      switch (linkBug.type) {
        case "Waiting":
          counts.waiting++;
          break;
        case "Blocked":
          counts.blocked++;
          break;
        case "Linked":
          counts.linked++;
          break;
      }
    });

    return counts;
  };

  // Helper function to get dependency icon
  const getDependencyIcon = (type: string) => {
    switch (type) {
      case "waiting":
        return <ErrorOutlineIcon sx={{ fontSize: 12, color: "#ff9800" }} />;
      case "blocked":
        return <BlockIcon sx={{ fontSize: 12, color: "#f44336" }} />;
      case "linked":
        return <LinkIcon sx={{ fontSize: 12, color: "#2196f3" }} />;
      case "related":
        return (
          <PeopleAltOutlinedIcon sx={{ fontSize: 12, color: "#9c27b0" }} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Draggable
        draggableId={optimisticTask?._id || optimisticTask?.id}
        index={index}
        key={optimisticTask?._id || optimisticTask?.id}
      >
        {(provided, snapshot) => (
          <Box
            data-task-id={optimisticTask._id}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={
              {
                // Ensure proper positioning during drag
                // position: snapshot.isDragging ? "relative" : "relative",
                // zIndex: snapshot.isDragging ? 1000 : "auto",
                // Add smooth transitions when not dragging
                // transition: snapshot.isDragging ? "none" : "transform 0.2s ease",
              }
            }
            style={
              {
                // Apply draggable styles
                // ...provided.draggableProps.style,
              }
            }
          >
            <Card
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              onClick={
                DEFEKT_TRACKER.VIEW(myPermissions) ? handleCardClick : undefined
              }
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              sx={{
                mb: showSubtaskCreation || showSubtasks ? 0 : 2,
                borderRadius: "6px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  transform: "translateY(-2px)",
                },
                cursor: "pointer",
                backgroundColor: theme.palette.background.paper,
                borderLeft: "3px solid",
                borderLeftColor:
                  severityColorMap[optimisticTask?.severity] || "#667eea",
                overflow: "visible",
                position: "relative",
                transition: "all 0.15s ease",
                ...(subGroupBy !== "None" && {
                  top: "auto !important",
                  left: "auto !important",
                }),
              }}
            >
              <Fade in={isHovering}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "flex",
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "4px",
                    border: `1px solid white ${theme.palette.text.primary}`,
                    padding: "2px",
                    color: theme.palette.text.primary,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
                    zIndex: 2,
                  }}
                >
                  <Tooltip
                    title={
                      doneStatusId
                        ? t("Mark as Done")
                        : t("Set Done Status in Project Settings")
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={handleDoneStatusIconClick}
                      sx={{ padding: "4px" }}
                      disabled={
                        isOptimisticUpdating ||
                        isUpdating ||
                        !DEFEKT_TRACKER.CREATE(myPermissions) ||
                        !DEFEKT_TRACKER.UPDATE(myPermissions)
                      }
                    >
                      {isUpdating || isOptimisticUpdating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CheckIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  {!doneStatusId && (
                    <Menu
                      anchorEl={doneStatusAnchorEl}
                      open={Boolean(doneStatusAnchorEl)}
                      onClose={handleDoneStatusMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      MenuListProps={{
                        onClick: (event) => event.stopPropagation(),
                      }}
                    >
                      <MenuItem
                        onClick={handleGoToProjectSettings}
                        sx={{
                          textDecoration: "underline",
                          fontSize: "0.75rem",
                        }}
                      >
                        {t("Set Done Status")}
                      </MenuItem>
                    </Menu>
                  )}

                  <Tooltip title={t("Add subtask")}>
                    <IconButton
                      size="small"
                      onClick={handleAddSubtask}
                      sx={{ padding: "4px" }}
                      disabled={
                        isUpdating ||
                        !DEFEKT_TRACKER.CREATE(myPermissions) ||
                        !DEFEKT_TRACKER.UPDATE(myPermissions)
                      }
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("Edit title/description")}>
                    <IconButton
                      size="small"
                      onClick={handleEdit}
                      sx={{
                        padding: "4px",
                        color:
                          editingTitle || editingDescription
                            ? "primary.main"
                            : "default",
                      }}
                      disabled={
                        isUpdating ||
                        !DEFEKT_TRACKER.CREATE(myPermissions) ||
                        !DEFEKT_TRACKER.UPDATE(myPermissions)
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("More options")}>
                    <IconButton
                      size="small"
                      onClick={handleMoreOptions}
                      sx={{ padding: "4px" }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Fade>

              <CardContent
                sx={{
                  padding: "10px !important",
                  "&:last-child": { pb: "10px !important" },
                  fontSize: "13px",
                }}
              >
                {/* Header: Priority (clickable) and ID on left */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: "6px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {/* Clickable Priority Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor:
                          DEFEKT_TRACKER.CREATE(myPermissions) ||
                          DEFEKT_TRACKER.UPDATE(myPermissions)
                            ? "pointer"
                            : "default",
                        padding: "2px",
                        borderRadius: "3px",
                        "&:hover": {
                          backgroundColor:
                            DEFEKT_TRACKER.CREATE(myPermissions) ||
                            DEFEKT_TRACKER.UPDATE(myPermissions)
                              ? "rgba(0, 0, 0, 0.04)"
                              : "transparent",
                        },
                        "& > *": {
                          width: "16px !important",
                          height: "16px !important",
                          minWidth: "16px !important",
                          minHeight: "16px !important",
                        },
                      }}
                      onClick={
                        DEFEKT_TRACKER.CREATE(myPermissions) ||
                        DEFEKT_TRACKER.UPDATE(myPermissions)
                          ? handleSeverityClick
                          : undefined
                      }
                    >
                      {optimisticTask?.severity ? (
                        getSeverityIcon(optimisticTask?.severity)
                      ) : (
                        <MoreHorizIcon
                          sx={{
                            fontSize: 16,
                            color: "#9ca3af",
                          }}
                        />
                      )}
                    </Box>

                    {optimisticTask?.bugKey && (
                      <span
                        style={{
                          color: "#6366f1",
                          fontFamily: "'SF Mono', Monaco, monospace",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {optimisticTask.bugKey}
                      </span>
                    )}
                  </Box>
                </Box>

                {/* Title */}
                <Box sx={{ mb: "4px" }}>
                  {editingTitle ? (
                    <ClickAwayListener onClickAway={handleSaveTitleEdit}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        inputRef={titleInputRef}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveTitleEdit();
                          if (e.key === "Escape") setEditingTitle(false);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ flex: 1 }}
                        disabled={
                          isOptimisticUpdating ||
                          isUpdating ||
                          !DEFEKT_TRACKER.CREATE(myPermissions) ||
                          !DEFEKT_TRACKER.UPDATE(myPermissions)
                        }
                      />
                    </ClickAwayListener>
                  ) : (
                    <Tooltip title={optimisticTask?.summary}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#1f2937",
                          wordBreak: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.4,
                        }}
                      >
                        {optimisticTask?.summary}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>

                {/* Dependencies Section */}
                {(optimisticTask?.linkBugs?.length > 0 ||
                  optimisticTask?.relatedBugsCount > 0) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    {(() => {
                      const counts = getDependencyCounts(
                        optimisticTask.linkBugs || [],
                        optimisticTask.relatedBugsCount || 0,
                      );
                      return (
                        <>
                          {counts.waiting > 0 && (
                            <Tooltip title={t("Waiting on dependencies")} arrow>
                              <Chip
                                icon={getDependencyIcon("waiting")}
                                label={counts.waiting}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "9px",
                                  backgroundColor: "#fff3e0",
                                  color: "#f57c00",
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    paddingLeft: "4px",
                                    paddingRight: "6px",
                                  },
                                  "& .MuiChip-icon": {
                                    fontSize: "12px",
                                    marginLeft: "4px",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {counts.blocked > 0 && (
                            <Tooltip title={t("Blocked dependencies")} arrow>
                              <Chip
                                icon={getDependencyIcon("blocked")}
                                label={counts.blocked}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "9px",
                                  backgroundColor: "#ffebee",
                                  color: "#d32f2f",
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    paddingLeft: "4px",
                                    paddingRight: "6px",
                                  },
                                  "& .MuiChip-icon": {
                                    fontSize: "12px",
                                    marginLeft: "4px",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {counts.linked > 0 && (
                            <Tooltip title={t("Linked dependencies")} arrow>
                              <Chip
                                icon={getDependencyIcon("linked")}
                                label={counts.linked}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "9px",
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    paddingLeft: "4px",
                                    paddingRight: "6px",
                                  },
                                  "& .MuiChip-icon": {
                                    fontSize: "12px",
                                    marginLeft: "4px",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {counts.related > 0 && (
                            <Tooltip title={t("My consumers")} arrow>
                              <Chip
                                icon={getDependencyIcon("related")}
                                label={counts.related}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "9px",
                                  backgroundColor: "#f3e5f5",
                                  color: "#7b1fa2",
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    paddingLeft: "4px",
                                    paddingRight: "6px",
                                  },
                                  "& .MuiChip-icon": {
                                    fontSize: "12px",
                                    marginLeft: "4px",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}

                {/* Tags */}
                {optimisticTask?.tags && optimisticTask?.tags?.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mb: "6px",
                    }}
                  >
                    {optimisticTask?.tags?.map((tag, index) => (
                      <Chip
                        key={index}
                        label={typeof tag === "string" ? tag : tag.tagName}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "10px",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          color: theme.palette.primary.main,
                          "& .MuiChip-label": {
                            paddingLeft: "6px",
                            paddingRight: "6px",
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Description */}
                <Box
                  sx={{
                    fontSize: "11px",
                    color:
                      optimisticTask?.description &&
                      stripHtmlTags(optimisticTask.description).trim()
                        ? "#6b7280"
                        : "#b0b0b0",
                    mb: "8px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.4,
                    fontStyle:
                      optimisticTask?.description &&
                      stripHtmlTags(optimisticTask.description).trim()
                        ? "normal"
                        : "italic",
                  }}
                >
                  {optimisticTask?.description &&
                  stripHtmlTags(optimisticTask.description).trim()
                    ? stripHtmlTags(optimisticTask.description)
                    : t("No description found")}
                </Box>

                {/* Footer: Category/Date on left, Comments/Assignee on right */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: "8px",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {/* Category/Type */}
                    {optimisticTask?.type && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "9px",
                          padding: "2px 5px",
                          background: "#f3f4f6",
                          borderRadius: "3px",
                          color: "#4b5563",
                        }}
                      >
                        {optimisticTask.type}
                      </Box>
                    )}
                    {/* Due Date */}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "10px",
                        color: isDueDateApproaching() ? "#dc2626" : "#9ca3af",
                        fontWeight: isDueDateApproaching() ? 600 : 400,
                        cursor:
                          DEFEKT_TRACKER.CREATE(myPermissions) ||
                          DEFEKT_TRACKER.UPDATE(myPermissions)
                            ? "pointer"
                            : "default",
                      }}
                      onClick={
                        DEFEKT_TRACKER.CREATE(myPermissions) ||
                        DEFEKT_TRACKER.UPDATE(myPermissions)
                          ? handleDueDateClick
                          : undefined
                      }
                    >
                      {optimisticTask?.dueDate
                        ? formatDate(optimisticTask.dueDate)
                        : t("No date")}
                    </Box>
                  </Box>

                  {/* Right side: Comments and Assignees */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {/* Comments */}
                    <Tooltip title={t("Comments")}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                          cursor: "pointer",
                        }}
                        onClick={
                          DEFEKT_TRACKER.CREATE(myPermissions) ||
                          DEFEKT_TRACKER.UPDATE(myPermissions)
                            ? handleCommentsClick
                            : undefined
                        }
                      >
                        <ChatBubbleOutlineIcon
                          sx={{ fontSize: 14, color: "#9ca3af" }}
                        />
                        <Typography sx={{ fontSize: "10px", color: "#9ca3af" }}>
                          {optimisticTask?.commentCount || 0}
                        </Typography>
                      </Box>
                    </Tooltip>

                    {/* Assignees */}
                    {optimisticTask?.assignTo?.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor:
                            DEFEKT_TRACKER.CREATE(myPermissions) ||
                            DEFEKT_TRACKER.UPDATE(myPermissions)
                              ? "pointer"
                              : "default",
                        }}
                        onClick={
                          DEFEKT_TRACKER.CREATE(myPermissions) ||
                          DEFEKT_TRACKER.UPDATE(myPermissions)
                            ? handleAssigneesClick
                            : undefined
                        }
                      >
                        {optimisticTask.assignTo
                          .slice(0, 2)
                          .map((item: any, index: number) => {
                            const assigneeData = getAssigneeData(item);
                            if (!assigneeData) return null;

                            return (
                              <Tooltip
                                key={assigneeData._id}
                                title={assigneeData.fullName}
                              >
                                <Avatar
                                  sx={{
                                    width: 25,
                                    height: 25,
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    background: getBackgroundColor(
                                      assigneeData._id,
                                      projectId,
                                    ),
                                    marginLeft: index > 0 ? "-7px" : 0,
                                    border: "2px solid white",
                                  }}
                                >
                                  {getInitials(assigneeData.fullName)}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                        {optimisticTask.assignTo.length > 2 && (
                          <Avatar
                            sx={{
                              width: 25,
                              height: 25,
                              fontSize: "10px",
                              fontWeight: 600,
                              background: "#9ca3af",
                              marginLeft: "-7px",
                              border: "2px solid white",
                            }}
                          >
                            +{optimisticTask.assignTo.length - 2}
                          </Avatar>
                        )}
                      </Box>
                    ) : (
                      <Tooltip title={t("Unassigned")}>
                        <Box
                          sx={{
                            width: 25,
                            height: 25,
                            borderRadius: "50%",
                            background: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor:
                              DEFEKT_TRACKER.CREATE(myPermissions) ||
                              DEFEKT_TRACKER.UPDATE(myPermissions)
                                ? "pointer"
                                : "default",
                          }}
                          onClick={
                            DEFEKT_TRACKER.CREATE(myPermissions) ||
                            DEFEKT_TRACKER.UPDATE(myPermissions)
                              ? handleAssigneesClick
                              : undefined
                          }
                        >
                          <PersonOutlineIcon
                            sx={{
                              fontSize: 16,
                              color: "#9ca3af",
                            }}
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {task?.isRecurring && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <Tooltip title={t("Recurring task")}>
                      <LoopOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: "text.secondary",
                        }}
                      />
                    </Tooltip>
                  </Box>
                )}
                {subtasksCount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1.5,
                      color: "#757575",
                      fontSize: "12px",
                      backgroundColor: "#f5f5f5",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": {
                        backgroundColor: "#e0e0e0",
                      },
                    }}
                    onClick={toggleSubtasks}
                  >
                    {isLoadingSubtasks ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 0.5 }} />
                        <Typography sx={{ fontSize: "12px" }}>
                          {t("Loading subtasks...")}
                        </Typography>
                      </>
                    ) : (
                      <>
                        {showSubtasks ? (
                          <KeyboardArrowUpIcon
                            fontSize="small"
                            sx={{ mr: 0.5 }}
                          />
                        ) : (
                          <KeyboardArrowDownIcon
                            fontSize="small"
                            sx={{ mr: 0.5 }}
                          />
                        )}
                        <Typography sx={{ fontSize: "12px" }}>
                          {subtasksCount} {subtasksCount !== 1 ? t("subtasks") : t("subtask")}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
            <Collapse in={showSubtasks} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1, mb: 2 }}>
                {(subTaskData && subTaskData.length > 0
                  ? subTaskData
                  : optimisticTask.subTasks || []
                )?.map((subtask: any, idx: number) => (
                  <Subtask
                    key={subtask._id}
                    subtask={subtask}
                    parentTask={optimisticTask}
                    index={idx}
                    users={getProjectFilteredUsers()}
                    refreshColumn={refreshColumn}
                    availableStatus={availableStatus}
                  />
                ))}
              </Box>
            </Collapse>

            {showSubtaskCreation && (
              <Box ref={subtaskContainerRef}>
                <SubtaskCreationCard
                  parentTask={optimisticTask}
                  onClose={handleCloseSubtaskCreation}
                  onSave={handleSaveSubtask}
                  refreshColumn={refreshColumn}
                  projectDetails={projectDetails}
                />
              </Box>
            )}
          </Box>
        )}
      </Draggable>

      {/* More options menu */}
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={handleCloseMoreOptions}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {!DEFEKT_TRACKER.HIDE_UPDATE(myPermissions) && (
          <MenuItem
            onClick={handleEditOption}
            disabled={!DEFEKT_TRACKER.UPDATE(myPermissions)}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">{t("Edit task")}</Typography>
          </MenuItem>
        )}
        {!DEFEKT_TRACKER.HIDE_DELETE(myPermissions) && (
          <MenuItem
            onClick={handleDeleteOption}
            disabled={!DEFEKT_TRACKER.DELETE(myPermissions)}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            <Typography variant="body2" color="error">
              {t("Delete task")}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      <DatePickerPopover
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        selectedDate={optimisticTask?.dueDate}
        onDateSelect={(dateString: string) => {
          const date = dateString ? new Date(dateString) : null;
          handleDateSelect(date);
        }}
      />

      <CommentsPopover
        anchorEl={commentsAnchorEl}
        onClose={handleCloseComments}
        comments={comments}
        isLoading={isLoadingComments}
        taskName={optimisticTask?.summary}
        taskId={optimisticTask?._id}
        refreshColumn={refreshColumn}
        onTaskUpdate={onTaskUpdate}
        task={optimisticTask}
      />

      <Menu
        anchorEl={priorityAnchorEl}
        open={Boolean(priorityAnchorEl)}
        onClose={() => setPriorityAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem
          onClick={() => handlePriorityChange("Highest")}
          selected={optimisticTask?.severity === "Highest"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon
              sx={{ color: severityColorMap["Highest"] }}
            />
          </ListItemIcon>
          <Typography variant="body2" color="error">
            {t("Highest")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("High")}
          selected={optimisticTask?.severity === "High"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["High"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="warning.dark">
            {t("High")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("Medium")}
          selected={optimisticTask?.severity === "Medium"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["Medium"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="info.main">
            {t("Medium")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange("Low")}
          selected={optimisticTask?.severity === "Low"}
        >
          <ListItemIcon>
            <FiberManualRecordIcon sx={{ color: severityColorMap["Low"] }} />
          </ListItemIcon>
          <Typography variant="body2" color="success.main">
            {t("Low")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Assignee Popover - UPDATED to use filtered users */}
      <Popover
        anchorEl={assigneeAnchorEl}
        open={Boolean(assigneeAnchorEl)}
        onClose={() => setAssigneeAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: 300, maxHeight: 400, p: 1 },
        }}
      >
        <Box sx={{ p: 1, mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Assigned")}
          </Typography>

          {optimisticTask?.assignTo?.length > 0 ? (
            optimisticTask.assignTo.map((user: any, index: number) => {
              const assigneeData = getAssigneeData(user);
              if (!assigneeData) return null;

              return (
                <MenuItem
                  key={assigneeData._id}
                  onClick={() => handleAssigneeChange(assigneeData._id)}
                  sx={{ borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        backgroundColor: getBackgroundColor(
                          assigneeData._id,
                          projectId,
                        ),
                      }}
                    >
                      <Typography variant="subtitle2">
                        {getInitials(assigneeData.fullName)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1 }}>
                      {assigneeData.fullName}
                    </Typography>
                    <Checkbox checked={true} size="small" />
                  </Box>
                </MenuItem>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {t("No users assigned")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Suggestions")}
          </Typography>

          {filteredUsers?.map((user: any, index: number) => {
            // Check if user is already assigned by comparing IDs
            const isAssigned = optimisticTask?.assignTo?.some(
              (assignee: any) => {
                const assigneeData = getAssigneeData(assignee);
                return assigneeData?._id === user._id;
              },
            );

            if (isAssigned) return null;

            return (
              <MenuItem
                key={user._id}
                onClick={() => handleAssigneeChange(user._id)}
                sx={{ borderRadius: 1 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      backgroundColor: getBackgroundColor(user._id, projectId),
                    }}
                  >
                    <Typography variant="subtitle2">
                      {getInitials(user.fullName)}
                    </Typography>
                  </Avatar>
                  <Typography sx={{ flexGrow: 1 }}>{user.fullName}</Typography>
                  <Checkbox checked={false} size="small" />
                </Box>
              </MenuItem>
            );
          })}
        </Box>
      </Popover>

      {isModalOpen && (
        <TaskUpdateDrawer
          open={isModalOpen}
          onClose={handleClose}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={optimisticTask?._id}
          users={companyMemberData?.data?.decryptedData?.items}
          createdBy={optimisticTask?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          refreshColumn={refreshColumn}
          onTaskUpdate={onTaskUpdate}
          task={optimisticTask}
          reactFlow={reactFlow}
        />
      )}

      <DeleteConfirmModal
        open={isDeleteReply}
        onClose={() => {
          setIsDeleteReply(false);
        }}
        onConfirm={handleDelete}
        confirmMessage={t(
          "Are you sure you want to delete this {{type}}?",
          { type: optimisticTask?.type || t("task") },
        )}
        loading={isDeleteLoading}
        title={t("Delete Task")}
        okText={t("Ok")}
      />
    </>
  );
};

export default Task;
